// @flow
import { isValidElement } from "react";
import { View } from "extensions/marionette";
import Radio from "backbone.radio";
import { _ } from "extensions/underscore";
import { toast } from "extensions/toast";
import { groupBy, orderBy } from "lodash-es";

import strings from "common/strings";
import { store } from "common/store";
import { emitter } from "common/emitter";
import { request } from "common/components/request";
import { getters } from "common/components/getters/getters";
import { DatePicker } from "common/components/datePicker/datePicker";
import { pazienHistory } from "common/components/pazienHistory";
import { watch } from "common/components/watch";

import {
    UPDATE_COMPARISON_REPORT
} from "common/actions/comparison";

import {
    UPDATE_BENCHMARKS_REPORT
} from "common/actions/benchmarks";

import {
    FETCH_SAVED_REPORTS
} from "common/actions/merchant_reports";

import {
    SET_GLOBAL_DATE_RANGE_RAW
} from "common/actions/common";

import { parse } from "common/components/datePicker/dateMath";

import { saveReport } from "merchant/features/reports/saveReport/saveReportDialog";
import { ReportsPane } from "merchant/features/reports/reportsPane/reportsPane";

import template from "./headerBar.jst";
import actionsTemplate from "./actions.jst";

import "./headerBar.scss";

const actionParser = (str) => str.split(":");

export const HeaderBar = View.extend({
    className: "headerBar",
    template,
    regions: {
        dateSelector: ".daterange-selector"
    },
    ui: {
        pageName: "#page-name",
        sectionName: "#section-name",
        description: "#page-description"
    },
    events: {
        "click [data-action]": function(e) {
            e.preventDefault();
            
            // Skip any empty actions
            if (!e.currentTarget.dataset.action) { return; }
            
            this.handleAction(actionParser(e.currentTarget.dataset.action));
        }
    },
    
    initialize() {
        this.previousDateParams = {};
        this.listenTo(Radio.channel("main"), "update:actions", this.updateAvailableActions);
        emitter.on("update:pagename", this.updatePageName.bind(this));
        const view = this.getOption("mainView");
        if (view.getOption("reportId") && !view.getOption("section_id") && store.getState().merchant_reports.saved_reports.status !== "loaded") {
            store.dispatch(FETCH_SAVED_REPORTS());
        }
    },
    
    updatePageName(name) {
        this.$(".name").text(name);
    },
    
    getMostRelevantSectionFromComparison(data, id) {
        const saved = store.getState().merchant_reports.saved_reports.data.find((r) => r._id === id);
        const metrics = saved.metrics.map((m) => getters.findById(m));
        const mapCategoryToSection = (category) => {
            if (category === "sales" || category === "refunds") {
                return "settlements";
            }
            return category;
        };
        const sections = metrics.map((m) => ({
            section: mapCategoryToSection(m.category)
        }));
        const grouped = groupBy(sections, mapCategoryToSection("section"));
            
        return orderBy(Object.keys(grouped).map((k) => ({ section: k, count: grouped[k].length })), "count", "desc")[0].section;
    },
    
    findSectionOfReport(id) {
        return new Promise((resolve, reject) => {
            const view = this.getOption("mainView");
            if (view.getOption("section_id")) {
                resolve(view.getOption("section_id"));
            } else if (view.getOption("name" === "Benchmarks")) {
                resolve("benchmarks");
            } else if (store.getState().merchant_reports !== "loaded") {
                request("/merchant/:merchant_ref/user/reports/").then((data) => {
                    resolve(this.getMostRelevantSectionFromComparison(data, view.getOption("reportId")));
                });
            } else {
                resolve(this.getMostRelevantSectionFromComparison(store.getState().merchant_reports.saved_reports, view.getOption("reportId")));
            }
        });
    },
    
    handleAction([action, value] = []) {
        if (!action) {
            throw new Error("Action is empty");
        }
        
        // For now we only support actions around reports.
        if (action === "report" && !store.getState().common.user_permissions.has_saved_reports) {
            Radio.channel("main").request("disabled:permission", "has_saved_reports");
            return;
        }
        
        const view = this.getOption("mainView");
        if (action === "report" && value === "save_as") {
            // Show report-save dialog
            saveReport({
                report_type: "regular",
                name: view.getOption("name"),
                model: view.config,
                dates: Radio.channel("main").request("global:datepicker:get")
            });
        } else if (action === "report" && value === "save") {
            // Do we want to show a confirmation here?
            if (!view.config) {
                return;
            }
            view.config.original_report_data = view.config.toJSON();
            view.config.save(null).then(() => {
                if (typeof this.getOption("mainView").getOption("asyncHeaderBar") === "function") {
                    this.asyncRender({ ignoreSavedDatepickers: true });
                }
                store.dispatch(FETCH_SAVED_REPORTS());
                toast.success(strings.reports_save_success_message);
            }).catch(() => {
                toast.error(strings.reports_save_error_message);
            });
        } else if (action === "report" && value === "close") {
            // TODO - Comparison reports don't work here...not sure where to send them?  Back to reports page?
            this.findSectionOfReport().then((section) => {
                pazienHistory.navigateWithParams(`analytics/${section}`, {
                    trigger: true
                });
            });
        } else if (action === "report" && value === "showall") {
            Radio.channel("main").request("show:sidepane", new ReportsPane(), "left");
        } else if (action === "page" && value === "close_comparison") {
            pazienHistory.navigateWithParams(store.getState().comparison.navigateBack, {
                trigger: true
            });
        } else if (action === "comparison_report" && value === "save_as") {
            // Show comparison report save dialog
            saveReport({
                report_type: "comparison",
                name: view.getOption("name"),
                model: store.getState().comparison,
                dates: Radio.channel("main").request("global:datepicker:get")
            });
        } else if (action === "comparison_report" && value === "save") {
            UPDATE_COMPARISON_REPORT(store.dispatch, store).then(() => {
                if (typeof this.getOption("mainView").getOption("asyncHeaderBar") === "function") {
                    this.asyncRender();
                }
                toast.success(strings.reports_save_success_message);
            }, () => {
                toast.error(strings.reports_save_error_message);
            });
        } else if (action === "benchmarks_report" && value === "save_as") {
            // Show comparison report save dialog
            saveReport({
                report_type: "benchmarks",
                name: view.getOption("name"),
                model: store.getState().benchmarks,
                dates: Radio.channel("main").request("global:datepicker:get")
            });
        } else if (action === "benchmarks_report" && value === "save") {
            UPDATE_BENCHMARKS_REPORT(store.dispatch, store).then(() => {
                if (typeof this.getOption("mainView").getOption("asyncHeaderBar") === "function") {
                    this.asyncRender();
                }
                toast.success(strings.reports_save_success_message);
            }, () => {
                toast.success(strings.reports_save_error_message);
            });
        }
    },
    
    showDatePicker(opts = {}) {
        this.showChildView("dateSelector", new DatePicker({
            global: true,
            view: this.getOption("mainView"),
            has_datetype: true,
            has_date_comparison: true,
            has_data_availability: false, // store.getState().common.user_permissions.can_view_data_availability,
            ...opts
        }));
    },
    
    makeActionsStr(optionName = "actions") {
        const mainView = this.getOption("mainView");
        if (isValidElement(mainView.options.component)) {
            const actions = mainView.getOption(optionName) || [];
            return actionsTemplate({ actions }).trim();
        }
        return actionsTemplate({
            actions: _.result(this.getOption("mainView"), optionName, [])
        }).trim();
    },

    templateContext() {
        const view = this.getOption("mainView");
        let pageName = "";
        if (!(typeof view.getOption("asyncHeaderBar") === "function" || view.getOption("asyncName"))) {
            pageName = view.getOption("name") || view.getOption("component")?.props?.options?.name;
        }
        return {
            has_report_switcher: view.getOption("reportId"),
            showNameLoader: view.getOption("asyncName"),
            name: pageName,
            section: view.getOption("reportId") ? `${view.getOption("name")}: SAVED VIEW` : view.getOption("section") || view.getOption("component")?.props?.options?.section,
            description: view.getOption("description"),
            versionSwitch: this.makeActionsStr("versionSwitch"),
            actions: this.makeActionsStr(),
            hasDatePicker: view.getOption("hideDatePicker") !== true,
            comparison_has_previous_page: view.getOption("canClosePage") && store.getState().comparison.navigateBack
        };
    },
    
    updateAvailableActions() {
        this.$(".actions").html(this.makeActionsStr());
    },
    
    asyncRender({ ignoreSavedDatepickers = false } = {}) {
        // Generalize this logic later if needed on other pages
        const view = this.getOption("mainView");
        view.getOption("asyncHeaderBar").call(view, ({
            name, dates, datePickerId, calendars = ["day", "month"], maxDate
        } = {}) => {
            this.updatePageName(name);
            this.updateAvailableActions();
            
            // Store previous date params to a variable
            const state = store.getState();
            this.previousDateParams = {
                date_range: state.common.date_range,
                compare_to: state.common.compare_to
            };
            
            const datepickerHash = `${state.common.bu_ref}_${datePickerId}`;
            if (!ignoreSavedDatepickers && datePickerId && HeaderBar.customPreviousDateParams[datepickerHash]) {
                dates = `${HeaderBar.customPreviousDateParams[datepickerHash].start}_${HeaderBar.customPreviousDateParams[datepickerHash].end}`;
            }
    
            let start;
            let end;
            if (dates) {
                [start, end] = DatePicker.convertDurationToMoment(dates);
            } else {
                start = state.common.date_range.startDate;
                end = state.common.date_range.endDate;
            }
    
            const maxDateParsed = maxDate ? parse(maxDate) : void 0;
            this.showDatePicker({
                start,
                end,
                calendars,
                maxDate: maxDateParsed
            });
            
            if (view.config) {
                // Listen to config changes to allow saving when updating a report config
                this.listenTo(view.config, "change:display_options change:dates change:datetype change:qualifiers", this.updateAvailableActions);
            }
            
            // Subscribe to updates in startDate/endDate to preserve date updates in decoupled datepickers
            // This will help us to recover updated section's dates when navigating to them later in the app
            if (datePickerId) {
                this._reduxSubscriptions = [
                    "common.date_range.startDate",
                    "common.date_range.endDate"
                ].map((objectPath) => store.subscribe(watch(store.getState, objectPath)((newStartDate: string) => {
                    const newState = store.getState();
    
                    // Set dates if they're not empty
                    if (newState.common.date_range.startDate && newState.common.date_range.endDate) {
                        HeaderBar.customPreviousDateParams[`${state.common.bu_ref}_${datePickerId}`] = {
                            start: newState.common.date_range.startDate,
                            end: newState.common.date_range.endDate
                        };
                    }
                })));
            }
        });
    },
    
    onRender() {
        const view = this.getOption("mainView");
        
        if (typeof view.getOption("asyncHeaderBar") === "function") {
            this.asyncRender();
        } else if (!view.getOption("hideDatePicker")) {
            this.showDatePicker();
        }
    },
    
    onDestroy() {
        emitter.off("update:pagename", this.updatePageName.bind(this));
    
        if (Object.keys(this.previousDateParams).length) {
            SET_GLOBAL_DATE_RANGE_RAW(this.previousDateParams);
        }
    }
}, {
    customPreviousDateParams: {}
});
